import {
  INITIAL_PAYMENT_LINK_VALUES,
  IPaymentLinkFormValues,
} from '../schema';
import {
  IPaymentState,
  IValidatePaymentLinkResponse,
} from '../types/types';
import { getDefaultPaymentMethod } from './paymentMethods';

type ApiPaymentLink = IValidatePaymentLinkResponse['response'];

export interface IMappedPaymentLink {
  isExpired: boolean;
  paymentState: IPaymentState;
  formValues: IPaymentLinkFormValues;
}

export const createEmptyPaymentState = (
  link: string | undefined,
): IPaymentState => ({
  link,
  allowedPaymentMethods: [],
  plan: undefined,
  addons: [],
  couponId: null,
  couponCode: null,
  subtotal: 0,
  discountAmount: 0,
  finalPrice: 0,
  paidAt: undefined,
  submittedAt: undefined,
});

export const mapPaymentLinkResponse = (
  link: string,
  paymentLink: ApiPaymentLink,
): IMappedPaymentLink => {
  const allowedPaymentMethods = paymentLink.allowed_payment_methods ?? [];

  const addons = (paymentLink.addons ?? []).map(addon => ({
    ...addon,
    total: addon.total ?? addon.amount * (addon.item_count || 1),
  }));

  const planAmount = paymentLink.plan_value ?? 0;
  const subtotal =
    paymentLink.subtotal ??
    addons.reduce((total, addon) => total + addon.total, planAmount);
  const discountAmount = paymentLink.discount_amount ?? 0;

  const paymentState: IPaymentState = {
    link,
    allowedPaymentMethods,
    plan: {
      id: paymentLink.plan_id ?? '',
      name: paymentLink.plan_name,
      amount: planAmount,
    },
    addons,
    couponId: paymentLink.coupon_id,
    couponCode: paymentLink.coupon_code ?? null,
    subtotal,
    discountAmount,
    finalPrice: paymentLink.final_price ?? subtotal - discountAmount,
    paidAt: paymentLink.paid_at,
    submittedAt: paymentLink.submitted_at,
  };

  const defaultPaymentMethod = getDefaultPaymentMethod(allowedPaymentMethods);
  const customer = paymentLink.customer;

  const formValues: IPaymentLinkFormValues = {
    ...INITIAL_PAYMENT_LINK_VALUES,
    ...(defaultPaymentMethod && {
      paymentMethodSelected: defaultPaymentMethod,
    }),
    ...(customer && {
      email: customer.email ?? '',
      document: customer.document ?? '',
      zipCode: customer.zipcode ?? '',
      state: customer.state ?? '',
      city: customer.city ?? '',
      address: customer.street ?? '',
      addressNumber: customer.street_number ?? '',
      addressComplement: customer.complement ?? '',
    }),
  };

  return {
    isExpired: paymentLink.is_expired,
    paymentState,
    formValues,
  };
};
